import { useState } from 'react'
import React from 'react'

import { F0Button, StandardLayout } from '@factorialco/f0-react'
import { OneModal, Tabs } from '@factorialco/f0-react/dist/experimental'
import { CheckDouble, MoneyBag } from '@factorialco/f0-react/icons/app'
import { AttendanceSidepanelCompensationBlockFragment } from 'generated/resources/graphql'
import { Moment } from 'moment'

import i18n from 'modules/core/lib/i18n'

import CompensationModalWrapper from 'modules/attendance/components/ClockIn/TabsBreakdown/CompensationModalWrapper'
import { useEmployeeSidepanelNavigation } from 'modules/attendance/hooks/useEmployeeSidepanelNavigation'
import { SidePanelTab } from 'modules/attendance/lib/sidepanelTabTypes'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { PeriodState } from 'modules/attendance/lib/types'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'

import Overview from './Overview'
import TimeCategoriesBreakdown from './TimeCategoriesBreakdown'
import { generateDailyTimeCategories } from './TimeCategoriesBreakdown/utils'

type Props = {
  tabFromNavigation: SidePanelTab
  timeSheetData: ReturnType<typeof useTimesheetData>
  timeSheetDate: Moment
  employeeId: number
}

const CompensationPannel = ({
  tabFromNavigation,
  timeSheetData,
  timeSheetDate,
  employeeId,
}: Props) => {
  const { employeesSettings, balances, reviewsStats } = timeSheetData

  const dailyTimeCategories = generateDailyTimeCategories(balances)
  const isPeriodApproved = reviewsStats?.state === PeriodState.approved

  // TODO: Remove this logic once the feature flag dev_compensations is removed
  const payrollCompensationEnabled =
    EmployeesSettingsUtils.hasPayrollCompensationEnabled(employeesSettings)

  const timeoffCompensationEnabled =
    EmployeesSettingsUtils.hasTimeoffCompensationEnabled(employeesSettings)

  const compensationEnabled = payrollCompensationEnabled || timeoffCompensationEnabled

  const [selectedCompensationBlocks, setSelectedCompensationBlocks] = useState<
    AttendanceSidepanelCompensationBlockFragment[]
  >([])

  const [openCompensationModal, setOpenCompensationModal] = React.useState(false)

  const { closePanel } = useEmployeeSidepanelNavigation({
    tabFromNavigation,
    compensationEnabled,
    timeSheetDate,
    employeeId,
  })

  const handleCompensate = () => {
    setOpenCompensationModal(true)
  }

  const defaultTabId = 'compensation-overview'

  const [activeTabId, setActiveTabId] = useState(defaultTabId)

  return (
    <OneModal isOpen onClose={closePanel} position="right">
      <OneModal.Header title={'Compensation details'} />
      <OneModal.Content>
        <StandardLayout>
          <Tabs
            tabs={[
              {
                label: i18n.t('clock_in.sidepanel.compensation_overview.label'),
                id: 'compensation-overview',
              },
              {
                label: i18n.t('clock_in.sidepanel.compensation_time_categories_breakdown.label'),
                id: 'compensation-time-categories',
              },
            ]}
            activeTabId={activeTabId}
            setActiveTabId={setActiveTabId}
          />
          {activeTabId === 'compensation-overview' && (
            <Overview
              setSelectedCompensationBlocks={setSelectedCompensationBlocks}
              employeeId={employeeId}
            />
          )}
          {activeTabId === 'compensation-time-categories' && (
            <TimeCategoriesBreakdown dailyTimeCategories={dailyTimeCategories} />
          )}
        </StandardLayout>
      </OneModal.Content>
      <>
        {activeTabId === 'compensation-overview' && selectedCompensationBlocks.length > 0 && (
          <OneModal.Footer>
            <F0Button
              label={
                isPeriodApproved
                  ? i18n.t('time_tracking.table_collection.employee_summary.actions.compensate')
                  : i18n.t('clock_in.sidepanel.compensation_overview.approve_and_compensate')
              }
              icon={isPeriodApproved ? MoneyBag : CheckDouble}
              onClick={handleCompensate}
            />
          </OneModal.Footer>
        )}

        <CompensationModalWrapper
          employeeId={employeeId}
          selectedCompensationBlocks={selectedCompensationBlocks}
          timeSheetData={timeSheetData}
          timeSheetDate={timeSheetDate}
          onClose={closePanel}
          openModal={openCompensationModal}
          setOpenModal={setOpenCompensationModal}
        />
      </>
    </OneModal>
  )
}

export default CompensationPannel
